import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STALE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

type SyncStatus = "fresh" | "stale" | "error" | "running" | "loading";

const useLastOrderSync = () => {
  return useQuery({
    queryKey: ["last-order-sync"],
    queryFn: async () => {
      // First get the workflow id
      const { data: workflow } = await supabase
        .from("workflows")
        .select("id")
        .eq("name", "sync-lightspeed-orders")
        .maybeSingle();

      if (!workflow) return null;

      const { data, error } = await supabase
        .from("workflow_executions")
        .select("id, status, started_at, completed_at, error_message")
        .eq("workflow_id", workflow.id)
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    staleTime: 30000,
    refetchInterval: 60000,
  });
};

const OrdersSyncBanner = () => {
  const queryClient = useQueryClient();
  const { data: lastSync, isLoading, refetch } = useLastOrderSync();
  const [syncing, setSyncing] = useState(false);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel("order-sync-banner")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "workflow_executions" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["last-order-sync"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const handleSyncNow = useCallback(async () => {
    setSyncing(true);
    try {
      const { error } = await supabase.functions.invoke("sync-lightspeed-orders", {
        method: "POST",
      });
      if (error) throw error;
      toast.success("Sync gestart");
      // Refetch after a short delay to pick up the new execution
      setTimeout(() => refetch(), 2000);
    } catch (e: any) {
      toast.error("Sync starten mislukt", { description: e.message });
    } finally {
      setSyncing(false);
    }
  }, [refetch]);

  // Determine status
  let status: SyncStatus = "loading";
  let timeAgo = "";

  if (!isLoading && lastSync) {
    const isRunning = lastSync.status === "running" || lastSync.status === "pending";
    const isFailed = lastSync.status === "failed";
    const refTime = lastSync.completed_at || lastSync.started_at;
    const age = refTime ? Date.now() - new Date(refTime).getTime() : Infinity;

    if (isRunning) {
      status = "running";
    } else if (isFailed) {
      status = "error";
    } else if (age > STALE_THRESHOLD_MS) {
      status = "stale";
    } else {
      status = "fresh";
    }

    if (refTime) {
      timeAgo = formatDistanceToNow(new Date(refTime), { addSuffix: true, locale: nl });
    }
  }

  if (isLoading) {
    return null; // Don't flash a banner while loading
  }

  if (!lastSync) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4" />
        <span>Geen sync-data gevonden</span>
        <Button variant="ghost" size="xs" onClick={handleSyncNow} disabled={syncing} className="ml-auto">
          {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : "Sync nu"}
        </Button>
      </div>
    );
  }

  const config: Record<SyncStatus, { bg: string; border: string; icon: React.ReactNode; label: string }> = {
    fresh: {
      bg: "bg-green-500/10",
      border: "border-green-500/30",
      icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      label: `Laatste sync ${timeAgo}`,
    },
    stale: {
      bg: "bg-yellow-500/10",
      border: "border-yellow-500/30",
      icon: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
      label: `Sync verouderd — ${timeAgo}`,
    },
    error: {
      bg: "bg-destructive/10",
      border: "border-destructive/30",
      icon: <XCircle className="h-4 w-4 text-destructive" />,
      label: `Sync mislukt — ${timeAgo}`,
    },
    running: {
      bg: "bg-blue-500/10",
      border: "border-blue-500/30",
      icon: <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />,
      label: "Sync bezig…",
    },
    loading: { bg: "", border: "", icon: null, label: "" },
  };

  const c = config[status];

  return (
    <div className={cn("flex items-center gap-2 rounded-lg border px-4 py-2 text-sm", c.bg, c.border)}>
      {c.icon}
      <span className="font-medium">{c.label}</span>
      {status === "error" && lastSync.error_message && (
        <span className="text-muted-foreground truncate max-w-xs" title={lastSync.error_message}>
          — {lastSync.error_message}
        </span>
      )}
      <div className="ml-auto flex items-center gap-1">
        <Button variant="ghost" size="xs" onClick={() => refetch()} title="Status verversen">
          <RefreshCw className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="xs"
          onClick={handleSyncNow}
          disabled={syncing || status === "running"}
        >
          {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : "Sync nu"}
        </Button>
      </div>
    </div>
  );
};

export default OrdersSyncBanner;
